import type { Auth0ContextInterface } from '@auth0/auth0-react';
import type { QueryClient } from '@tanstack/react-query';
import { getIdeasByIds } from '../../../api/ideas';
import { formatBigMonetaryValue } from '../../ideaNiv/Funcs';
import { ideaKeys } from '../../../hooks/ideaPage/ideaKeys';

export const EXPLORE_IDEAS_LIST_PAGE_SIZE = 10;

export interface ExploreIdeaImpactMetric {
  key: string;
  label: string;
  formattedValue: string;
}

export interface ExploreIdeaListRowData {
  id: string;
  title: string;
  statusKey: string;
  statusLabel: string;
  statusColor: string;
  componentName: string;
  componentObj: Record<string, unknown> | null;
  ownerName: string;
  ownerInitials: string;
  impactMetrics: ExploreIdeaImpactMetric[];
  evidenceCount: number | null;
  isMissing: boolean;
}

export interface ExploreIdeasListPageRawData {
  pageIdeaIds: string[];
  ideasById: Record<string, Record<string, unknown> | null>;
  aggregationsById: Record<string, Record<string, unknown>>;
  missingIdeaIds: string[];
}

export interface ExploreIdeasListPageData {
  rows: ExploreIdeaListRowData[];
}

interface IdeaStatusMeta {
  key?: string;
  label?: string;
  color?: string;
}

interface ImpactPropertyConfig {
  propertyName?: string;
  label?: string;
  fieldPath?: string;
  groupBy?: boolean;
  impact?: { index?: number; prefix?: string; groupByPath?: string };
}

function readOwnerName(idea: Record<string, unknown> | null | undefined): string {
  if (!idea) {
    return '';
  }
  const ownerObj = idea.ownerObj as { name?: string } | undefined;
  if (ownerObj?.name?.trim()) {
    return ownerObj.name.trim();
  }
  const owner = idea.owner as { name?: string } | undefined;
  return owner?.name?.trim() ?? '';
}

function readComponentName(idea: Record<string, unknown> | null | undefined): string {
  if (!idea) {
    return '';
  }
  const componentObj = idea.componentObj as { name?: string } | undefined;
  if (componentObj?.name?.trim()) {
    return componentObj.name.trim();
  }
  const component = idea.component as { name?: string } | undefined;
  return component?.name?.trim() ?? '';
}

function readComponentObj(
  idea: Record<string, unknown> | null | undefined
): Record<string, unknown> | null {
  if (!idea) {
    return null;
  }
  const componentObj = idea.componentObj as Record<string, unknown> | undefined;
  if (componentObj && typeof componentObj === 'object') {
    return componentObj;
  }
  const component = idea.component as Record<string, unknown> | undefined;
  return component && typeof component === 'object' ? component : null;
}

function readEvidenceCount(
  idea: Record<string, unknown> | null | undefined,
  aggregations: Record<string, unknown> | null
): number | null {
  const evidenceRollup = aggregations?.evidencesCount as { total?: number } | undefined;
  if (typeof evidenceRollup?.total === 'number') {
    return evidenceRollup.total;
  }
  const assignedRollup = aggregations?.assignedEvidencesCount as { total?: number } | undefined;
  if (typeof assignedRollup?.total === 'number') {
    return assignedRollup.total;
  }
  const evidences = idea?.evidences;
  if (Array.isArray(evidences)) {
    return evidences.length;
  }
  const evidenceCount = idea?.evidenceCount;
  if (typeof evidenceCount === 'number') {
    return evidenceCount;
  }
  return null;
}

function getImpactPropertyValue(
  aggregations: Record<string, unknown> | null,
  property: ImpactPropertyConfig
): number {
  if (!aggregations || !property.propertyName) {
    return 0;
  }
  const impactData = aggregations[property.propertyName] as Record<string, unknown> | number | undefined;
  if (impactData == null) {
    return 0;
  }
  if (property.groupBy && property.impact?.groupByPath) {
    const groupKey = property.impact.groupByPath;
    const group = (impactData as Record<string, unknown>)[groupKey] as
      | { count?: number; total?: number }
      | undefined;
    return group?.count ?? group?.total ?? 0;
  }
  if (typeof impactData === 'object' && 'total' in impactData) {
    return Number((impactData as { total?: number }).total ?? 0);
  }
  return typeof impactData === 'number' ? impactData : 0;
}

function buildOwnerInitials(ownerName: string): string {
  const parts = ownerName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '';
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
}

function resolveTopImpactProperties(
  impactProperties: ImpactPropertyConfig[]
): ImpactPropertyConfig[] {
  return [...impactProperties]
    .sort((left, right) => (left.impact?.index ?? 0) - (right.impact?.index ?? 0))
    .slice(0, 2);
}

function buildImpactMetrics(
  aggregations: Record<string, unknown> | null,
  impactProperties: ImpactPropertyConfig[]
): ExploreIdeaImpactMetric[] {
  return resolveTopImpactProperties(impactProperties).map((property) => {
    const value = getImpactPropertyValue(aggregations, property);
    const isCompanies = ['orgId', 'company._id'].includes(property.fieldPath ?? '');
    const formattedValue = isCompanies
      ? String(value)
      : formatBigMonetaryValue(value, property.impact?.prefix ?? '$') ?? '—';

    return {
      key: property.propertyName ?? property.label ?? 'impact',
      label: property.label || property.propertyName || '',
      formattedValue,
    };
  });
}

function mapIdeaToRowData(
  ideaId: string,
  idea: Record<string, unknown> | null,
  aggregations: Record<string, unknown> | null,
  ideaStatuses: IdeaStatusMeta[],
  impactProperties: ImpactPropertyConfig[],
  isMissing: boolean
): ExploreIdeaListRowData {
  const statusKey = String(idea?.status ?? '');
  const statusMeta = ideaStatuses.find(
    (status) => String(status.key ?? '') === statusKey
  );
  const ownerName = readOwnerName(idea);

  return {
    id: ideaId,
    title: String(idea?.title ?? '').trim() || 'Untitled idea',
    statusKey,
    statusLabel: String(statusMeta?.label ?? statusKey ?? '—') || '—',
    statusColor: String(statusMeta?.color ?? '#868E96'),
    componentName: readComponentName(idea),
    componentObj: readComponentObj(idea),
    ownerName,
    ownerInitials: buildOwnerInitials(ownerName),
    impactMetrics: buildImpactMetrics(aggregations, impactProperties),
    evidenceCount: readEvidenceCount(idea, aggregations),
    isMissing,
  };
}

export function getExploreIdeasListPageSlice(ideaIds: string[], page: number): string[] {
  const pageStartIndex = (page - 1) * EXPLORE_IDEAS_LIST_PAGE_SIZE;
  return ideaIds.slice(pageStartIndex, pageStartIndex + EXPLORE_IDEAS_LIST_PAGE_SIZE);
}

function isValidMongoObjectId(value: string): boolean {
  return /^[a-fA-F0-9]{24}$/.test(value);
}

export async function fetchExploreIdeasListPageRawData(
  pageIdeaIds: string[],
  auth0: Auth0ContextInterface,
  queryClient: QueryClient
): Promise<ExploreIdeasListPageRawData> {
  if (pageIdeaIds.length === 0) {
    return {
      pageIdeaIds: [],
      ideasById: {},
      aggregationsById: {},
      missingIdeaIds: [],
    };
  }

  const validIdeaIds = pageIdeaIds.filter(isValidMongoObjectId);
  const ideasById: Record<string, Record<string, unknown> | null> = {};
  const aggregationsById: Record<string, Record<string, unknown>> = {};
  const missingIdeaIds: string[] = pageIdeaIds.filter((ideaId) => !validIdeaIds.includes(ideaId));
  validIdeaIds.forEach((ideaId) => {
    ideasById[ideaId] = null;
  });

  if (validIdeaIds.length > 0) {
    try {
      const { ideas } = await getIdeasByIds(validIdeaIds, auth0);
      ideas.forEach((idea) => {
        const ideaId = String(idea._id ?? '');
        if (!ideaId) {
          return;
        }
        ideasById[ideaId] = idea;
        const aggregations = idea.aggregations as Record<string, unknown> | undefined;
        if (aggregations && typeof aggregations === 'object') {
          aggregationsById[ideaId] = aggregations;
        }
        queryClient.setQueryData(ideaKeys.detail(ideaId), idea);
      });
      validIdeaIds.forEach((ideaId) => {
        if (!ideasById[ideaId]) {
          missingIdeaIds.push(ideaId);
        }
      });
    } catch {
      missingIdeaIds.push(...validIdeaIds.filter((ideaId) => !missingIdeaIds.includes(ideaId)));
      validIdeaIds.forEach((ideaId) => {
        ideasById[ideaId] = null;
      });
    }
  }

  return {
    pageIdeaIds,
    ideasById,
    aggregationsById,
    missingIdeaIds,
  };
}

export function mapExploreIdeasListPageData(
  rawData: ExploreIdeasListPageRawData,
  ideaStatuses: IdeaStatusMeta[],
  impactProperties: ImpactPropertyConfig[]
): ExploreIdeasListPageData {
  const rows = rawData.pageIdeaIds.map((ideaId) =>
    mapIdeaToRowData(
      ideaId,
      rawData.ideasById[ideaId] ?? null,
      rawData.aggregationsById[ideaId] ?? null,
      ideaStatuses,
      impactProperties,
      rawData.missingIdeaIds.includes(ideaId)
    )
  );

  return { rows };
}
